import type { WishlistsState } from '~/composables/useWishlists/wishlists';
import type { WishlistModelTransformed } from '~/core/entities/api/ApiWishlist';

/**
 * Plugin to initialize wishlist store globally
 * This ensures the useFetch runs immediately when the app loads
 */
export default defineNuxtPlugin(async () => {
	const { user, loggedIn } = useUserSession();
	const { cookie: favoritesCookie } = useFavoritesCookie();
	const router = useRouter();

	const wishlistKey = computed(() => {
		const key = `${user.value?.id || 'anonymous'}:wishlist-${favoritesCookie.value || 'no-cookie'}`;
		return key;
	});

	const { data, refresh, status } = useFetch<WishlistModelTransformed[]>(
		'/api/wishlist',
		{
			key: wishlistKey.value,
			// Client-only fetching to improve SSR cache hit ratio
			// The server renders a generic (logged-out) state, and the client
			// hydrates with actual user data after detecting the session
			server: false,
			immediate: false,
			watch: [wishlistKey]
		}
	);

	// Trigger fetch on client when user is logged in
	if (import.meta.client && loggedIn.value) {
		refresh();
	}

	const wishlistsState = useState<WishlistsState>('wishlists', () => ({
		currentWishlistId: '',
		propertyCount: 0,
		wishlists: [],
		favoritePropertyIds: []
	}));

	const dataWatcher = watch(
		data,
		newData => {
			if (!newData?.length) {
				wishlistsState.value = {
					currentWishlistId: '',
					propertyCount: 0,
					wishlists: [],
					favoritePropertyIds: []
				};
				return;
			}

			// Update state reactively
			const allFavoriteIds = new Set<string>();
			newData.forEach(w =>
				w.properties.forEach(p => allFavoriteIds.add(p._id))
			);

			wishlistsState.value = {
				currentWishlistId: wishlistsState.value.currentWishlistId,
				propertyCount: allFavoriteIds.size,
				wishlists: newData,
				favoritePropertyIds: Array.from(allFavoriteIds)
			};
		},
		{ immediate: true }
	);

	const loginWatcher = watch(
		[loggedIn, user],
		async ([newLoggedIn, newUser], [oldLoggedIn, oldUser]) => {
			// Only trigger on actual state changes, not on initial load
			if (newLoggedIn && newUser && oldLoggedIn === false) {
				await refresh();
			} else if (!newLoggedIn && oldLoggedIn === true) {
				wishlistsState.value = {
					currentWishlistId: '',
					propertyCount: 0,
					wishlists: [],
					favoritePropertyIds: []
				};
			}
		},
		{ immediate: false }
	);

	// Watch for route changes and refresh wishlist data.
	// Client-only: this only needs to react to SPA navigation, and registering
	// afterEach during SSR leaks the plugin closure for the process lifetime
	// (the hook is never disposed on the server).
	let stopRouteWatch: (() => void) | undefined;
	if (import.meta.client) {
		stopRouteWatch = router.afterEach((to, from) => {
			// Only refresh if user is logged in
			if (!loggedIn.value) {
				return;
			}

			// Check if the actual path changed (not just query parameters)
			const pathChanged = to.path !== from.path;
			if (pathChanged) {
				refresh();
			}
		});
	}

	// Create a global isFetching ref based on status
	const isFetching = computed(() => {
		return status.value === 'pending';
	});

	// Cleanup watchers on app unmount
	if (getCurrentScope()) {
		onScopeDispose(() => {
			dataWatcher();
			loginWatcher();
			stopRouteWatch?.();
		});
	}

	// Provide methods globally
	return {
		provide: {
			wishlistRefresh: refresh,
			wishlistStatus: status,
			wishlistIsFetching: isFetching
		}
	};
});
